import * as Styled from "./CreateEventPage.styles";

import { APIError, Checkbox, PageContainer } from "../../components";
import LoadingModal from "../../modals/LoadingModal/LoadingModal";
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CREATE_EVENT,
  GET_USERS_EMAILS,
  TRIGGER_EVENT_EMAIL_ACTION,
} from "../../services/endpoints";
import {
  ClearFormModal,
  DraftEventsModal,
  EventOpsConsentModal,
  PreviewEmailModal,
  RoomFinderModal,
} from "../../modals";
import {
  DetailsSection,
  EventFeedbackSection,
  PrimaryContactSection,
  SignupSurveySection,
} from "./sections";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import {
  combineDayJSToUnix,
  sanitizeAndPrepareEmailDescription,
} from "../../utils/utils";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";

import { Event } from "../../types/event";
import { NEW_EVENT_CREATED } from "../../store/actions/newEventActions";
import { Room } from "../../types/room";
import { RootState } from "../../store/store";
import { UserState } from "../../types/redux";
import axiosAPI from "../../services/axios";
import formOptions from "./formOptions";
import { gatherDefault64 } from "../../modals/PreviewEmailModal/images";
import { getAllEvents } from "../../store/actions/eventsActions";
import { getAllUserEvents } from "../../store/actions/userEventsActions";
import imageCompression from "browser-image-compression";
import { useAppDispatch } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

type CreateEventForm = {
  signUpCreator: boolean;
  title: string;
  description: string;
  eventDate?: Dayjs | null;
  startDateTime?: Dayjs | null;
  endDateTime?: Dayjs | null;
  dressCode: string;
  admissionCost: number;
  eventImageFile: File | null;
  format: string;
  office: string;
  eventTags: string[];
  eventType: string;
  rooms: Room[];
  location: string;
  transportationParkingDetails: string;
  primaryContacts: { email: string; name: string }[];
  includeTeamsLink: string;
  teamsLink?: string;
  feedbackLink: string;
  includeSignUpSurvey: string;
  signUpSurvey: { type: string; question: string }[];
  recipientList: string[];
  bypassSendEmail: boolean;
  googleMapsLocationLink?: string;
};

const CreateEventPage = () => {
  const currentTime = dayjs();
  const environment = process.env.REACT_APP_ENVIRONMENT || "local";
  const roundToNearest30Minutes = (dateTime: Dayjs) => {
    const minutes = dateTime.minute();
    const roundedMinutes = Math.round(minutes / 30) * 30;
    return dateTime.startOf("hour").add(roundedMinutes, "minute");
  };

  const userInfo = useSelector((state: any) => {
    const userState = state.user as UserState;
    return userState;
  });

  const selectUserEventsDraftLoading = useSelector(
    (state: RootState) => state.userEvents.loading
  );

  const selectUserEventsDraft = useSelector(
    (state: RootState) => state.userEvents.draft
  );

  const { loading: roomsLoading } = useSelector(
    (state: RootState) => state.room
  );

  const [showPreviewEmailModal, setShowPreviewEmailModal] = useState(false);

  const formMethods = useForm({
    defaultValues: {
      title: "",
      description: "",
      googleMapsLocationLink: "",
      eventDate: dayjs(),
      startDateTime: roundToNearest30Minutes(currentTime),
      endDateTime: roundToNearest30Minutes(currentTime).add(30, "minute"),
      dressCode: "",
      admissionCost: 0,
      format: "",
      eventImageFile: null,
      eventTags: [],
      eventType: "",
      office: "Boston & Hartford (New England)",
      signUpCreator: true,
      location: "",
      transportationParkingDetails: "",
      primaryContacts: [
        {
          email: userInfo.email,
          name: userInfo.name,
        },
      ],
      includeTeamsLink: "no",
      teamsLink: "",
      feedbackLink: "",
      includeSignUpSurvey: "no",
      signUpSurvey: [],
      rooms: [],
      recipientList: [],
      bypassSendEmail: false,
    },
    mode: "onChange",
  });
  const [showClearFormModal, setShowClearFormModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createEventError, setCreateEventError] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showDraftEventsModal, setShowDraftEventsModal] = useState(false);
  const [showRoomFinderModal, setShowRoomFinderModal] = useState(false);
  const [roomFinderError, setRoomFinderError] = useState(false);
  const [roomFinderErrorMessage, setRoomFinderErrorMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [finalizingTimeout, setFinalizingTimeout] =
    useState<NodeJS.Timeout | null>(null);

  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const includeSignUpSurvey = formMethods.watch("includeSignUpSurvey");
  const rooms = formMethods.watch("rooms");
  const eventType = formMethods.watch("eventType");
  const bypassSendEmail = formMethods.watch("bypassSendEmail");

  const format = formMethods.watch("format");
  const { errors } = formMethods.formState;
  const hasErrors = Object.keys(errors).length > 0;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await axiosAPI.request({
          url: GET_USERS_EMAILS(),
          method: "GET",
        });

        const transformedUsers = data.result.users.map(
          (user: { email: string; firstName: string; lastName: string }) => ({
            email: user.email,
            name: `${user.firstName} ${user.lastName}`.trim(),
          })
        );

        setUsers(transformedUsers);
      } catch (error) {
        console.error("Error fetching user emails:", error);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    if (eventType === "Off-site") {
      formMethods.setValue("rooms", []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType]);

  useEffect(() => {
    if (format === "Virtual") {
      formMethods.setValue("rooms", []);
      formMethods.setValue("location", "");
      formMethods.setValue("eventType", "Off-site");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format]);

  const toISOString = (timestamp: number) =>
    new Date(timestamp * 1000).toISOString();

  const compressAndConvertImage = async (imageFile: File) => {
    const options = {
      maxSizeMB: 3.0,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };
    const compressedFile = await imageCompression(imageFile, options);
    return await imageCompression.getDataUrlFromFile(compressedFile);
  };

  const createEventEmailPayload = async (
    {
      title,
      description,
      startDateTime,
      endDateTime,
      format,
      eventOrganizerName,
      eventOrganizerEmail,
      location,
      googleMapsLocationLink,
      recipientList,
    }: Event,
    rooms: Room[],
    eventImage: string
  ) => {
    return {
      action: "create",
      eventTitle: title,
      eventDescription: description,
      eventStartDateTime: toISOString(startDateTime),
      eventEndDateTime: toISOString(endDateTime),
      roomLocation: rooms.map((room: Room) => room.email).join(";"),
      eventInvitees: recipientList.join(";"),
      eventOrganizer: eventOrganizerName,
      eventOrganizerEmail: eventOrganizerEmail,
      eventFormat: format,
      eventImage: eventImage === "" ? gatherDefault64 : eventImage,
      eventLocation: location,
      eventLocationLink: googleMapsLocationLink || "",
    };
  };

  const sendEventEmail = async (
    event: Event,
    rooms: Room[],
    eventImage: string
  ) => {
    const payload = await createEventEmailPayload(event, rooms, eventImage);
    await axiosAPI.post(TRIGGER_EVENT_EMAIL_ACTION(event.eventId), payload);
  };

  const createEventObj = (data: any, eventHero: string, status: string) => {
    return {
      ...data,
      primaryContacts: data.primaryContacts.map(
        (contact: { name: String; email: string }) => contact.name
      ),
      description: sanitizeAndPrepareEmailDescription(data.description),
      startDateTime: combineDayJSToUnix(
        data.eventDate as Dayjs,
        data.startDateTime as Dayjs
      ),
      endDateTime: combineDayJSToUnix(
        data.eventDate as Dayjs,
        data.endDateTime as Dayjs
      ),
      location: (() => {
        if (data.format === "Virtual") {
          return "Virtual";
        } else if (data.eventType === "Off-site") {
          return data.location;
        } else if (data.eventType === "On-site") {
          if (data.rooms.length === 0) {
            const officeLocationMapping: { [key: string]: string } = {
              Boston: "399 Boylston St #1000, Boston, MA 02116",
              Hartford: "185 Asylum St #3201, Hartford, CT 06103",
              "Boston & Hartford (New England)":
                "399 Boylston St #1000, Boston, MA 02116 (Boston Office) & 185 Asylum St #3201, Hartford, CT 06103 (Hartford Office)",
            };
            return officeLocationMapping[data.office] || data.office;
          } else {
            return data.rooms.map((room: Room) => room.name).join(", ");
          }
        }
        return "";
      })(),
      googleMapsLocationLink: (() => {
        // Prefer an explicit link if provided by the form. If not provided,
        // fall back to building a search URL from the textual location.
        if (data.googleMapsLocationLink) return data.googleMapsLocationLink;
        if (data.format === "Virtual") return "";
        const finalLocation =
          data.eventType === "Off-site"
            ? data.location
            : data.eventType === "On-site" && data.rooms.length === 0
            ? (() => {
                const officeLocationMapping: { [key: string]: string } = {
                  Boston: "399 Boylston St #1000, Boston, MA 02116",
                  Hartford: "185 Asylum St #3201, Hartford, CT 06103",
                  "Boston & Hartford (New England)":
                    "399 Boylston St #1000, Boston, MA 02116 (Boston Office) & 185 Asylum St #3201, Hartford, CT 06103 (Hartford Office)",
                };
                return officeLocationMapping[data.office] || data.office;
              })()
            : data.rooms.map((room: Room) => room.name).join(", ");
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          finalLocation.trim()
        )}`;
      })(),
      recipientList: data.bypassSendEmail
        ? environment === "prod"
          ? process.env.EVENTS_EMAIL
          : userInfo.email
        : data.recipientList,
      rooms: data.rooms,
      eventHero,
      status,
      eventOrganizerName: userInfo.name,
      eventOrganizerTitle: userInfo.jobTitle,
      eventOrganizerEmail: userInfo.email,
      participantEmail: "NA",
      signUpCreator: status === "DRAFT" ? false : data.signUpCreator,
    };
  };

  const createEvent = async (
    newEventObj: any,
    rooms: Room[],
    isDraft: boolean = true,
    isPublish: boolean = true,
    eventImage: string = ""
  ) => {
    try {
      setLoading(true);
      setModalOpen(true);

      const response = await axiosAPI.post(CREATE_EVENT(), newEventObj);
      const { result }: { result: Event } = response.data;

      if (!isDraft) {
        setLoadingMessage("Creating your event...");
        setLoadingMessage("Sending out invitations...");
        const finalizingTimer = setTimeout(() => {
          setLoadingMessage("Finalizing the details...");
        }, 10000);

        try {
          await sendEventEmail(result, rooms, eventImage);
        } finally {
          clearTimeout(finalizingTimer);
        }
      }

      if (isPublish) {
        dispatch(getAllEvents());
        dispatch(getAllUserEvents(userInfo.email));
        dispatch({
          type: NEW_EVENT_CREATED,
          payload: { id: result.eventId },
        });
        navigate("/events");
      } else {
        dispatch(getAllEvents());
        dispatch(getAllUserEvents(userInfo.email));
        navigate(`/draft/${result.eventId}`, {
          state: {
            alert: {
              severity: "success",
              message: "Your draft has successfully been saved!",
            },
          },
        });
      }
      if (finalizingTimeout) {
        clearTimeout(finalizingTimeout);
        setFinalizingTimeout(null);
      }

      setTimeout(() => setModalOpen(false), 1500);
    } catch (error: any) {
      const errorMessage = error.response?.data?.data;

      if (
        errorMessage.includes("Room is not available for the specified time")
      ) {
        setRoomFinderError(true);
        setRoomFinderErrorMessage(
          "The room that you have selected is no longer available. Please try again."
        );
      } else {
        setCreateEventError(true);
      }
    } finally {
      setLoading(false);
      setModalOpen(false);
    }
  };

  const consentModalCallback = async () => {
    const data = formMethods.getValues();

    setLoading(true);
    try {
      const eventImage = data.eventImageFile
        ? await compressAndConvertImage(data.eventImageFile as File)
        : "";
      const newEventObj = createEventObj(data, eventImage, "ACTIVE");
      await createEvent(newEventObj, data.rooms, false, false, eventImage);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const saveDraft = async () => {
    const data = formMethods.getValues();
    setLoading(true);
    try {
      const eventImage = data.eventImageFile
        ? await compressAndConvertImage(data.eventImageFile as File)
        : "";
      const newEventObj = createEventObj(data, eventImage, "DRAFT");
      await createEvent(newEventObj, data.rooms, true, false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit: SubmitHandler<CreateEventForm> = async (data) => {
    setShowConsentModal(true);
  };

  return (
    <>
      <EventOpsConsentModal
        open={showConsentModal}
        onClose={() => {
          setShowConsentModal(false);
        }}
        onConfirm={consentModalCallback}
      />
      <RoomFinderModal
        name={"rooms"}
        control={formMethods.control}
        setValue={formMethods.setValue}
        open={showRoomFinderModal}
        onClose={() => {
          setShowRoomFinderModal(false);
        }}
        roomFinderError={roomFinderError}
        roomFinderErrorMessage={roomFinderErrorMessage}
        setRoomFinderError={setRoomFinderError}
        setRoomFinderErrorMessage={setRoomFinderErrorMessage}
      />
      <DraftEventsModal
        open={showDraftEventsModal}
        onClose={() => {
          setShowDraftEventsModal(false);
        }}
      />
      <ClearFormModal
        open={showClearFormModal}
        onClose={() => {
          setShowClearFormModal(false);
        }}
        reset={formMethods.reset}
      />
      <LoadingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        message={loadingMessage}
      />
      <PageContainer>
        {createEventError ? (
          <APIError
            onClick={() => {
              setCreateEventError(false);
            }}
          />
        ) : (
          <Styled.PaddedContainer>
            <FormProvider {...formMethods}>
              <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                <PreviewEmailModal
                  open={showPreviewEmailModal}
                  onClose={() => {
                    setShowPreviewEmailModal(false);
                  }}
                  formData={formMethods.getValues()}
                />
                {roomFinderError && (
                  <Alert
                    severity="error"
                    sx={{ marginBottom: theme.spacing(2) }}
                  >
                    {roomFinderErrorMessage}
                  </Alert>
                )}
                <Styled.HeaderStack
                  direction={{ mobile: "column", desktop: "row" }}
                  width="100%"
                >
                  <Styled.HeaderText variant="h1" gutterBottom>
                    Create An Event
                  </Styled.HeaderText>
                  <Styled.SignUpCheckboxContainer>
                    <Typography> Sign me up for this event</Typography>
                    <Checkbox
                      name="signUpCreator"
                      control={formMethods.control}
                      defaultChecked
                    />
                  </Styled.SignUpCheckboxContainer>
                </Styled.HeaderStack>
                {selectUserEventsDraftLoading ? (
                  <Styled.SubtitleActionTextLoading variant="modalSubtitle">
                    {`Loading draft events...`}
                  </Styled.SubtitleActionTextLoading>
                ) : (
                  <Styled.SubtitleActionText
                    variant="modalSubtitle"
                    onClick={() => {
                      setShowDraftEventsModal(true);
                    }}
                  >
                    {`View draft events (${selectUserEventsDraft.length})`}
                  </Styled.SubtitleActionText>
                )}
                <DetailsSection
                  formMethods={formMethods}
                  formOptions={formOptions}
                  environment={environment}
                  userInfo={userInfo}
                  format={format}
                  eventType={eventType}
                  roomFinderError={roomFinderError}
                  roomFinderErrorMessage={roomFinderErrorMessage}
                  rooms={rooms}
                  roomsLoading={roomsLoading}
                  setShowRoomFinderModal={setShowRoomFinderModal}
                  bypassSendEmail={bypassSendEmail}
                />
                <PrimaryContactSection
                  formMethods={formMethods}
                  users={users}
                />
                <SignupSurveySection
                  formMethods={formMethods}
                  includeSignUpSurvey={includeSignUpSurvey}
                />
                <EventFeedbackSection />
                {loading && !modalOpen ? (
                  <Styled.LoaderContainer>
                    <CircularProgress />
                  </Styled.LoaderContainer>
                ) : (
                  <Box>
                    <Box
                      display="flex"
                      justifyContent={{ mobile: "center", desktop: "end" }}
                    >
                      {hasErrors && (
                        <Styled.ErrorMessage>
                          Please correct all errors before publishing the event
                        </Styled.ErrorMessage>
                      )}
                      {roomFinderError && (
                        <Styled.ErrorMessage>
                          {roomFinderErrorMessage}
                        </Styled.ErrorMessage>
                      )}
                    </Box>
                    <Styled.FormActionStack
                      direction={{ mobile: "column", desktop: "row" }}
                      spacing={theme.spacing(1)}
                      width="100%"
                      alignItems={{ mobile: "flex-start", desktop: "flex-end" }}
                    >
                      <Styled.ActionTextButton
                        onClick={() => {
                          setShowClearFormModal(true);
                        }}
                      >
                        Clear Event Form
                      </Styled.ActionTextButton>
                      <Styled.ActionTextButton
                        onClick={() => {
                          setShowPreviewEmailModal(true);
                        }}
                      >
                        Preview Event Email
                      </Styled.ActionTextButton>
                      <Stack
                        direction={{ mobile: "column", desktop: "row" }}
                        spacing={theme.spacing(1)}
                        width="100%"
                        justifyContent="flex-end"
                      >
                        <Styled.ActionButton
                          variant="outlined"
                          onClick={saveDraft}
                        >
                          Save Event Draft
                        </Styled.ActionButton>
                        <Styled.ActionButton
                          variant="contained"
                          type="submit"
                          disabled={
                            (roomsLoading || roomFinderError) &&
                            eventType === "On-site"
                          }
                        >
                          {(roomsLoading || roomFinderError) &&
                          eventType === "On-site"
                            ? roomsLoading
                              ? "Loading rooms..."
                              : "Fix Error In Room Finder"
                            : "Publish Event"}
                        </Styled.ActionButton>
                      </Stack>
                    </Styled.FormActionStack>
                  </Box>
                )}
              </form>
            </FormProvider>
          </Styled.PaddedContainer>
        )}
      </PageContainer>
    </>
  );
};

export default CreateEventPage;
